import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

import { AdminUrlBuilder } from '../../../../../../shared/admin/api/admin-url-builder';
import { AbstractFormComponent } from '../../../../../../shared/crud/component/form.component';
import { IDropdownSearcherOptions } from '../../../../../../shared/ui/component/dropdown-searcher/dropdown-searcher.interfaces';
import {
    CampaignData,
    CategoryOptions,
    InventoryOption,
    SubPaper,
    SubPhotopass,
} from '../../models/inventory-option.model';
import { ProductAddForm } from './inventory-add.form';

@Component({
    templateUrl: './inventory-add-product.component.html',
    styleUrls: ['./inventory-add-product.component.scss'],
})
export class AdminInventoryAddProductModal extends AbstractFormComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() campaignsOptions: any[];
    @Input() dropdownSearchOptions: IDropdownSearcherOptions;

    @Output() onProductAdded = new EventEmitter<any>();

    private originalCampaignsOptionsSubject: BehaviorSubject<CampaignData[]> = new BehaviorSubject<CampaignData[]>([]);
    campaignsOptionsSubject: BehaviorSubject<CampaignData[]> = new BehaviorSubject<CampaignData[]>([]);
    campaignsOptions$: Observable<CampaignData[]> = this.campaignsOptionsSubject.asObservable();
    categoryChange: Subscription;
    campaignChange: Subscription;

    categoryOptions: InventoryOption[] = CategoryOptions;
    subPaper: InventoryOption[] = SubPaper;
    subPhotopass: InventoryOption[] = SubPhotopass;
    subProducts: InventoryOption[] = [];
    selectedCampaign: number = null;

    constructor(
        formBuilder: ProductAddForm,
        httpClient: HttpClient,
        toastrService: ToastrService,
        urlBuilder: AdminUrlBuilder,
        router: Router,
        route: ActivatedRoute,
        public modalRef: BsModalRef
    ) {
        super(formBuilder, httpClient, toastrService, router, route, urlBuilder);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.formMode = 'create';
        this.categoryChange = this.form.get('category').valueChanges.subscribe((newCategory) => {
            this.onCategoryChange(newCategory);
        });
        this.campaignChange = this.form.get('campaignUuid').valueChanges.subscribe((newCampaignUuid) => {
            this.onCampaignChange(newCampaignUuid);
        });
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();

        if (this.formMode === 'update') {
            this.populate().subscribe();
        }

        this.originalCampaignsOptionsSubject.next([...this.campaignsOptions]);
        this.campaignsOptionsSubject.next(this.originalCampaignsOptionsSubject.getValue());
    }

    ngOnDestroy(): void {
        if (this.categoryChange) this.categoryChange.unsubscribe();
        if (this.campaignChange) this.campaignChange.unsubscribe();
    }

    protected getUrl(): string {
        return '/stock-item';
    }

    protected onSubmit(response: any): void {
        this.toastrService.success('Saved');
        this.onProductAdded.emit(response);
        this.channel.next(this.record);
        this.record = {};
        this.form.reset();
    }

    onCategoryChange(newCategory: string): void {
        const subcategory = this.form.get('subcategory');
        const products = this.form.get('products');

        if (newCategory === 'product') {
            subcategory.clearValidators();
            products.setValidators([Validators.required]);
        } else {
            products.clearValidators();
            subcategory.setValidators([Validators.required]);
        }

        subcategory.updateValueAndValidity();
        products.updateValueAndValidity();

        this.resetSelections();
    }

    onCampaignChange(newCampaignUuid: string): void {
        this.httpClient
            .get<any>(`${this.urlBuilder.buildUrl(this.getUrl())}/campaign/${newCampaignUuid}/products`)
            .subscribe(
                (response: any) => {
                    this.subProducts = this.mapResponseToInventoryOptions(response, newCampaignUuid);
                },
                (error: any) => {
                    this.loading = false;
                }
            );
        this.resetSelections();
    }

    private parseLabel(languageDefault: string, itemName: string): string {
        try {
            const parsedLabel = JSON.parse(itemName);
            let label: string;
            if (parsedLabel[languageDefault]) {
                label = parsedLabel[languageDefault];
            } else if (parsedLabel.en) {
                label = parsedLabel.en;
            } else {
                label = parsedLabel[Object.keys(parsedLabel)[0]];
            }
            return label;
        } catch (e) {
            return itemName;
        }
    }

    private mapResponseToInventoryOptions(response: any, newCampaignUuid: string): InventoryOption[] {
        const selectedCampaign = this.campaignsOptions.find((campaign) => campaign.campaignUuid === newCampaignUuid);

        return response
            .filter((item) => !item.deleteDate ||  !item.deleteDate.datetime) // Filter out items with deleteDate.datetime not null
            .map((item) => {
                const label: string = this.parseLabel(selectedCampaign.languageDefault, item.name);

                return {
                    label: label,
                    value: item.uuid,
                    checked: false,
                    isUsed: item.isUsed,
                };
            });
    }

    resetSelections(): void {
        this.form.get('subcategory').setValue('');
        this.form.get('products').setValue([]);
    }

    onSelectionChange(selection: string | string[]) {
        const category = this.form.get('category').value;
        if (category === 'product') {
            this.form.get('products').setValue(selection as string[]);
        } else if (category === 'paper' || category === 'photopass') {
            this.form.get('subcategory').setValue(selection as string);
        }
    }

    handleCampaignSearch(phrase: string) {
        const originalOptions = this.originalCampaignsOptionsSubject.getValue();

        if (!phrase) {
            this.campaignsOptionsSubject.next(originalOptions);
            return;
        }
        const filteredOptions = originalOptions.filter((option) => {
            return option.label.toLowerCase().includes(phrase.toLowerCase()) || '';
        });

        this.campaignsOptionsSubject.next(filteredOptions);
    }

    handleCampaignChange(selectedItem: CampaignData) {
        this.selectedCampaign = selectedItem.value;
        this.form.get('campaignUuid').setValue(selectedItem.campaignUuid);
    }

    submit(): void {
        this.formMode = 'create';
        if (this.form.invalid) {
            this.toastrService.error('Form contains incorrect data');
            return;
        }

        this.httpClient.post(`${this.urlBuilder.buildUrl(this.getUrl())}`, this.form.value).subscribe(
            (response: any) => {
                this.toastrService.success('Product added');
                this.modalRef.hide();
                this.onProductAdded.emit();
            },
            (error: any) => {
                this.toastrService.error(error.error ? error.error.message : 'Product has been not added');
            }
        );
    }
}
